import { useState, type FormEvent } from 'react';

const FORM_URL = 'https://readdy.ai/api/form/dah8knjp14a1h3mr6fag';

type Status = 'idle' | 'submitting' | 'success' | 'error';

export default function Contact() {
  const [status, setStatus] = useState<Status>('idle');
  const [formError, setFormError] = useState('');

  const handleSubmit = async (event: FormEvent<HTMLFormElement>) => {
    event.preventDefault();
    const form = event.currentTarget;
    const formData = new FormData(form);

    const honeypot = String(formData.get('website_alt') || '').trim();
    if (honeypot) {
      setStatus('success');
      setFormError('');
      return;
    }

    const payload = new URLSearchParams();
    formData.forEach((value, key) => {
      if (key === 'website_alt') return;
      const text = String(value);
      if (text.trim() !== '') payload.append(key, text);
    });

    setStatus('submitting');
    setFormError('');

    try {
      const response = await fetch(FORM_URL, {
        method: 'POST',
        headers: { 'Content-Type': 'application/x-www-form-urlencoded' },
        body: payload.toString(),
      });
      const responseText = await response.text();
      let parsed: {
        code?: string;
        message?: string;
        meta?: { message?: string; detail?: string };
      } | null = null;
      try {
        parsed = JSON.parse(responseText);
      } catch {
        parsed = null;
      }

      const serverMsg =
        parsed?.meta?.message || parsed?.message || parsed?.meta?.detail || responseText;
      const isSpam = typeof serverMsg === 'string' && serverMsg.toLowerCase().includes('spam');

      if (!response.ok || parsed?.code !== 'OK' || isSpam) {
        setStatus('error');
        setFormError(
          typeof serverMsg === 'string' && serverMsg.trim() !== ''
            ? serverMsg
            : '提交失败，请稍后重试。',
        );
        return;
      }

      setStatus('success');
      setFormError('');
      form.reset();
    } catch {
      setStatus('error');
      setFormError('网络异常，请稍后重试。');
    }
  };

  return (
    <section id="contact" className="w-full bg-background-200 py-16 md:py-24">
      <div className="w-full px-4 md:px-8">
        <div className="grid grid-cols-1 lg:grid-cols-12 gap-8 lg:gap-12">
          <div className="lg:col-span-5">
            <span className="text-xs font-medium tracking-widest text-primary-600 uppercase">
              联系与投递
            </span>
            <h2 className="font-heading text-2xl md:text-4xl font-bold text-foreground-950 mt-3 mb-4">
              有值得追踪的线索，告诉我们
            </h2>
            <p className="text-sm md:text-base text-foreground-600 leading-relaxed mb-8">
              把你看到的事件大致讲清楚就好，不要求措辞工整。信息越具体，我们越容易判断它属于哪一组对比线索。
            </p>

            <div className="space-y-5">
              <div className="flex items-start gap-4">
                <span className="w-10 h-10 shrink-0 flex items-center justify-center rounded-lg bg-background-50 text-primary-600">
                  <i className="ri-mail-line text-lg"></i>
                </span>
                <div>
                  <div className="text-sm font-medium text-foreground-950 mb-0.5">邮箱联系</div>
                  <a
                    href="mailto:contact@baokandian.example.com"
                    className="text-sm text-foreground-600 hover:text-primary-600 transition-colors cursor-pointer"
                  >
                    contact@baokandian.example.com
                  </a>
                </div>
              </div>

              <div className="flex items-start gap-4">
                <span className="w-10 h-10 shrink-0 flex items-center justify-center rounded-lg bg-background-50 text-primary-600">
                  <i className="ri-lightbulb-line text-lg"></i>
                </span>
                <div>
                  <div className="text-sm font-medium text-foreground-950 mb-0.5">什么样的线索更有用</div>
                  <p className="text-sm text-foreground-600 leading-relaxed">
                    能说明时间、涉及主体类型，以及你认为它和哪件事相似，这样的线索最容易被纳入对比。
                  </p>
                </div>
              </div>

              <div className="flex items-start gap-4">
                <span className="w-10 h-10 shrink-0 flex items-center justify-center rounded-lg bg-background-50 text-primary-600">
                  <i className="ri-time-line text-lg"></i>
                </span>
                <div>
                  <div className="text-sm font-medium text-foreground-950 mb-0.5">处理节奏</div>
                  <p className="text-sm text-foreground-600 leading-relaxed">
                    我们通常会在收到线索后先做初步核实，只有能交叉验证的内容才会进入案例库。
                  </p>
                </div>
              </div>
            </div>
          </div>

          <div className="lg:col-span-7">
            <div className="bg-background-50 border border-background-200 rounded-lg p-5 md:p-8">
              {status === 'success' ? (
                <div className="text-center py-12">
                  <span className="w-16 h-16 mx-auto flex items-center justify-center rounded-full bg-secondary-100 text-secondary-600 mb-5">
                    <i className="ri-check-line text-3xl"></i>
                  </span>
                  <h3 className="font-heading text-xl font-semibold text-foreground-950 mb-2">
                    已收到你的线索
                  </h3>
                  <p className="text-sm text-foreground-600 mb-6">
                    我们会先做初步核实，如判断具备对比价值，会把它编入对应的追踪线索。
                  </p>
                  <button
                    type="button"
                    onClick={() => setStatus('idle')}
                    className="px-5 py-2.5 rounded-md border border-background-300 text-sm font-medium text-foreground-800 hover:border-primary-400 hover:text-primary-600 transition-colors cursor-pointer whitespace-nowrap"
                  >
                    再投递一条
                  </button>
                </div>
              ) : (
                <form data-readdy-form onSubmit={handleSubmit} noValidate>
                  <div className="grid grid-cols-1 sm:grid-cols-2 gap-4 mb-4">
                    <div>
                      <label htmlFor="contact-name" className="block text-sm font-medium text-foreground-800 mb-2">
                        怎么称呼你
                      </label>
                      <input
                        id="contact-name"
                        name="name"
                        type="text"
                        required
                        placeholder="昵称即可"
                        className="w-full px-3.5 py-2.5 rounded-md border border-background-300 bg-background-50 text-sm text-foreground-900 placeholder:text-foreground-400 focus:outline-none focus:ring-2 focus:ring-primary-400 focus:border-primary-400"
                      />
                    </div>
                    <div>
                      <label htmlFor="contact-email" className="block text-sm font-medium text-foreground-800 mb-2">
                        邮箱
                      </label>
                      <input
                        id="contact-email"
                        name="email"
                        type="email"
                        required
                        placeholder="you@example.com"
                        className="w-full px-3.5 py-2.5 rounded-md border border-background-300 bg-background-50 text-sm text-foreground-900 placeholder:text-foreground-400 focus:outline-none focus:ring-2 focus:ring-primary-400 focus:border-primary-400"
                      />
                    </div>
                  </div>

                  <div className="mb-4">
                    <label htmlFor="contact-topic" className="block text-sm font-medium text-foreground-800 mb-2">
                      线索所属主题
                    </label>
                    <select
                      id="contact-topic"
                      name="topic"
                      defaultValue="content"
                      className="w-full px-3.5 py-2.5 rounded-md border border-background-300 bg-background-50 text-sm text-foreground-900 focus:outline-none focus:ring-2 focus:ring-primary-400 focus:border-primary-400 cursor-pointer"
                    >
                      <option value="content">文娱内容</option>
                      <option value="commerce">直播电商</option>
                      <option value="brand">品牌营销</option>
                      <option value="sports">体育赛场</option>
                      <option value="tech">科技发布</option>
                      <option value="variety">网络综艺</option>
                      <option value="other">其他</option>
                    </select>
                  </div>

                  <div className="mb-5">
                    <label htmlFor="contact-detail" className="block text-sm font-medium text-foreground-800 mb-2">
                      线索描述
                    </label>
                    <textarea
                      id="contact-detail"
                      name="detail"
                      rows={5}
                      required
                      maxLength={500}
                      placeholder="大致说说发生了什么、大概什么时间，以及你觉得它和哪件事相似。"
                      className="w-full px-3.5 py-2.5 rounded-md border border-background-300 bg-background-50 text-sm text-foreground-900 placeholder:text-foreground-400 focus:outline-none focus:ring-2 focus:ring-primary-400 focus:border-primary-400 resize-none"
                    ></textarea>
                    <p className="text-xs text-foreground-500 mt-2">最多 500 字，简要说明即可。</p>
                  </div>

                  <input
                    className="form-honeypot-field"
                    type="text"
                    name="website_alt"
                    tabIndex={-1}
                    autoComplete="off"
                    aria-hidden="true"
                    readOnly
                  />

                  {status === 'error' && formError && (
                    <div className="mb-4 px-4 py-3 rounded-md bg-primary-50 border border-primary-200 text-sm text-primary-700">
                      {formError}
                    </div>
                  )}

                  <button
                    type="submit"
                    disabled={status === 'submitting'}
                    className="w-full px-6 py-3 rounded-md bg-primary-500 text-background-50 font-medium hover:bg-primary-600 transition-colors cursor-pointer whitespace-nowrap disabled:opacity-60 disabled:cursor-not-allowed"
                  >
                    {status === 'submitting' ? '提交中…' : '提交线索'}
                  </button>
                </form>
              )}
            </div>
          </div>
        </div>
      </div>
    </section>
  );
}